import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import {
  motion,
  AnimatePresence,
  animate,
  useMotionValue,
  useMotionTemplate,
  useTransform,
  useMotionValueEvent,
  useReducedMotion,
} from 'framer-motion';
import {
  FaUserTie,
  FaExclamationCircle,
  FaArrowUp,
  FaSitemap,
  FaPlus,
  FaMinus,
  FaCrosshairs,
  FaExpandArrowsAlt,
  FaExpand,
  FaCompress,
  FaPrint,
} from 'react-icons/fa';
import {
  getGerenciaColor,
  tintOf,
  nodeKey,
  sortSubordinados,
  OrgStateBox,
  VACANTE_COLOR,
  normalizeGerencia,
} from './orgShared';

/**
 * Vista "Mapa": el organigrama como un lienzo que se despliega hacia abajo.
 *  - Una sola rama abierta: la cadena de mando desde la raiz hasta el foco,
 *    con los pares de cada nivel atenuados a los lados.
 *  - Click en alguien con equipo: su gente sale de su tarjeta y las lineas
 *    se dibujan desde el; la rama anterior se recoge hacia su jefe.
 *  - Franjas por nivel de cargo: cada tarjeta cae en la de su nivel, asi un
 *    coordinador que le reporta a un gerente no se ve a la par de los lideres.
 *  - La camara sigue al foco y encuadra su equipo, como hace Explorar.
 *  - Arrastrar mueve el mapa, la rueda acerca; las flechas navegan.
 */

// Geometria del lienzo (px en coordenadas del mundo)
// Tarjeta casi cuadrada: foto grande arriba, nombre y cargo centrados debajo.
// El layout necesita el alto exacto antes de pintar, por eso las medidas viven
// aqui: el texto de cada persona se mide (el cargo nunca se corta) y se suman.
const CARD_W = 208;
const GAP_X = 20;
const ROW_GAP = 84; // del borde inferior de una fila al tope de la siguiente
const BUS_OFFSET = 36; // altura del bus horizontal sobre la fila hija

const C_PAD_X = 14;
const C_PAD_TOP = 16;
const C_PHOTO = 56;
const C_GAP = 10; // foto -> nombre
const C_TEXT_GAP = 12; // texto -> pie
const C_FOOT_H = 34; // pie con el abanico de su equipo

const F_PAD_X = 16;
const F_PAD_TOP = 18;
const F_PHOTO = 68;
const F_GAP = 12;
const F_TEXT_GAP = 14;
const F_STATS_H = 56;

// El borde (hasta 1.5px por lado) come ancho y alto del contenido: se mide un
// poco mas angosto y se suma el borde, asi el texto medido nunca queda corto
const BORDER_ROOM = 4;
const compactHeight = (textH) =>
  C_PAD_TOP + C_PHOTO + C_GAP + textH + C_TEXT_GAP + C_FOOT_H + BORDER_ROOM;
const focusHeight = (textH) =>
  F_PAD_TOP + F_PHOTO + F_GAP + textH + F_TEXT_GAP + F_STATS_H + BORDER_ROOM;
// Estimados mientras no hay medida: nombre en una linea y cargo en dos
const TEXT_GUESS_C = 52;
const TEXT_GUESS_F = 86;

// Limites de zoom: manual y encuadre automatico
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 1.8;
const FIT_MIN = 0.6;
const FIT_MAX = 1.15;

// Margenes del encuadre: arriba vive el riel de linaje, abajo los controles
const PAD_X = 40;
const PAD_TOP = 76;
const PAD_BOTTOM = 68;

const MOVE_T = { type: 'spring', stiffness: 240, damping: 26, mass: 0.9 };
const SHAPE_T = { type: 'spring', stiffness: 320, damping: 34 };
const EXIT_T = { duration: 0.26, ease: [0.4, 0, 1, 1] };
const CAMERA_T = { type: 'spring', duration: 0.85, bounce: 0 };
const DOUBLE_CLICK_MS = 350;
// La animacion del navegador rechaza demoras no finitas: siempre entre 0 y 1.6s
const safeDelay = (d) => (Number.isFinite(d) ? Math.min(Math.max(d, 0), 1.6) : 0);

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

function buildMaps(roots) {
  const byKey = new Map();
  const parentOf = new Map();
  const stats = new Map(); // key -> { personas, vac } de todo su subarbol

  const walk = (node, parentKey) => {
    const key = nodeKey(node);
    byKey.set(key, node);
    parentOf.set(key, parentKey);
    let personas = 0;
    let vac = 0;
    (node.subordinados || []).forEach((child) => {
      const s = walk(child, key);
      const childVac = child.tipo === 'vacante';
      personas += s.personas + (childVac ? 0 : 1);
      vac += s.vac + (childVac ? 1 : 0);
    });
    const s = { personas, vac };
    stats.set(key, s);
    return s;
  };

  (roots || []).forEach((root) => walk(root, null));
  return { byKey, parentOf, stats };
}

// Conector en codo con esquinas redondeadas. Siempre la misma secuencia de
// comandos, para que framer pueda interpolar `d` cuando cambia la geometria.
function elbowAt(x1, y1, x2, y2, busY) {
  const dx = x2 - x1;
  const dir = Math.sign(dx);
  const r = Math.max(0, Math.min(14, Math.abs(dx) / 2, (busY - y1) / 2));
  const f = (n) => n.toFixed(1);
  return [
    `M ${f(x1)} ${f(y1)}`,
    `L ${f(x1)} ${f(busY - r)}`,
    `Q ${f(x1)} ${f(busY)} ${f(x1 + dir * r)} ${f(busY)}`,
    `L ${f(x2 - dir * r)} ${f(busY)}`,
    `Q ${f(x2)} ${f(busY)} ${f(x2)} ${f(busY + r)}`,
    `L ${f(x2)} ${f(y2)}`,
  ].join(' ');
}
const elbow = (x1, y1, x2, y2) => elbowAt(x1, y1, x2, y2, y2 - BUS_OFFSET);

// Franjas de nivel: el alto de cada tarjeta lo decide el nivel de su cargo
// (cargos_base.nivel), no la profundidad en el arbol. Asi un coordinador que
// le reporta directo a un gerente queda mas abajo que los lideres de ese gerente.
const TIER_LABEL = {
  1: 'Presidencia',
  2: 'Gerencia General',
  3: 'Gerencias',
  4: 'Líderes',
  5: 'Coordinadores',
  6: 'Especialistas',
  7: 'Analistas y asesores',
  8: 'Asistentes y operativos',
  9: 'Pasantes y apoyo',
  99: 'Sin nivel',
};
const SIN_NIVEL = 999;
const STEM_GAP = 48; // hueco por donde baja el tallo en las filas intermedias
const SLOT_TOP = 60; // la franja empieza este tanto por encima de sus tarjetas
const CHIP_H = 22;

const tierOf = (eff) => (eff >= SIN_NIVEL ? 99 : Math.floor(eff / 10));

// Nivel efectivo: nunca por encima (ni igual) que el de su jefe, por si un dato
// viene mal cargado; sin nivel va al fondo de su equipo.
function effectiveNivel(node, parentEff) {
  const own = typeof node.nivel === 'number' ? node.nivel : SIN_NIVEL;
  return parentEff == null ? own : Math.max(own, parentEff + 1);
}

// X de cada tarjeta de una fila. Si por debajo hay mas filas del mismo jefe,
// la fila se abre en dos y deja pasar el tallo por el centro; el sobrante de
// una fila impar alterna de lado para no cargar siempre el mismo.
function rowXs(n, cx, split, rowIdx) {
  const step = CARD_W + GAP_X;
  if (!split) {
    const x0 = cx - (n * CARD_W + (n - 1) * GAP_X) / 2;
    return Array.from({ length: n }, (_, i) => x0 + i * step);
  }
  const left = n % 2 === 1 && rowIdx % 2 === 0 ? Math.ceil(n / 2) : Math.floor(n / 2);
  const right = n - left;
  const xs = [];
  for (let i = 0; i < left; i += 1) {
    xs.push(cx - STEM_GAP / 2 - (left - i) * CARD_W - (left - i - 1) * GAP_X);
  }
  for (let i = 0; i < right; i += 1) xs.push(cx + STEM_GAP / 2 + i * step);
  return xs;
}

// ---------------------------------------------------------------------------
// Estructura completa bajo el foco (doble click): cada rama ocupa su propio
// tramo horizontal y el alto sigue siendo el nivel. Las ramas se empacan por
// su contorno fila a fila, contando tambien las lineas que bajan por las
// filas intermedias, asi ninguna linea atraviesa una tarjeta.
// ---------------------------------------------------------------------------
const LINE_PAD = 16; // margen de una linea vertical que cruza una fila

function addSpan(contour, row, lo, hi) {
  const cur = contour.get(row);
  if (!cur) contour.set(row, [lo, hi]);
  else contour.set(row, [Math.min(cur[0], lo), Math.max(cur[1], hi)]);
}

function mergeShifted(into, from, shift) {
  from.forEach(([lo, hi], row) => addSpan(into, row, lo + shift, hi + shift));
}

function tidyLayout(root, rootEff, rowOf) {
  const place = (node, eff) => {
    const row = rowOf.get(eff);
    const key = nodeKey(node);
    const contour = new Map([[row, [-CARD_W / 2, CARD_W / 2]]]);
    const items = [{ key, dx: 0 }];
    const edges = [];
    // Los de nivel mas alto primero (a la izquierda); orden estable
    const kids = sortSubordinados(node.subordinados)
      .map((ch) => ({ node: ch, eff: effectiveNivel(ch, eff) }))
      .sort((a, b) => a.eff - b.eff);
    if (!kids.length) return { contour, items, edges };

    const subs = kids.map((k) => ({ ...k, row: rowOf.get(k.eff), res: place(k.node, k.eff) }));
    const busRow = Math.min(...subs.map((sub) => sub.row));
    // La bajada de cada hijo cruza las filas entre el bus y su tarjeta
    subs.forEach((sub) => {
      for (let r = busRow; r < sub.row; r += 1) addSpan(sub.res.contour, r, -LINE_PAD, LINE_PAD);
    });
    const offs = [];
    const acc = new Map();
    subs.forEach((sub, i) => {
      let o = 0;
      if (i > 0) {
        o = -Infinity;
        sub.res.contour.forEach(([lo], r) => {
          const a = acc.get(r);
          if (a) o = Math.max(o, a[1] + GAP_X - lo);
        });
        if (o === -Infinity) o = offs[i - 1] + CARD_W + GAP_X;
      }
      offs.push(o);
      mergeShifted(acc, sub.res.contour, o);
    });
    // El jefe queda centrado sobre su primer y su ultimo hijo
    const mid = (offs[0] + offs[offs.length - 1]) / 2;
    mergeShifted(contour, acc, -mid);
    for (let r = row + 1; r < busRow; r += 1) addSpan(contour, r, -LINE_PAD, LINE_PAD);
    subs.forEach((sub, i) => {
      const shift = offs[i] - mid;
      sub.res.items.forEach((it) => items.push({ key: it.key, dx: it.dx + shift }));
      sub.res.edges.forEach((e) => edges.push(e));
      edges.push({ parentKey: key, childKey: nodeKey(sub.node), busRow });
    });
    return { contour, items, edges };
  };
  return place(root, rootEff);
}

/**
 * Posiciones de todo lo visible.
 *  - Familias: los reportes de cada eslabon de la cadena de mando, mas el
 *    equipo del foco. En los eslabones de arriba solo se ven los del mismo
 *    nivel o mas altos que el eslabon siguiente; los demas se resumen en una
 *    etiqueta "+N en otros niveles" (si no, chocarian con el equipo de abajo).
 *  - Filas: una por nivel presente (se comprimen los niveles vacios), y cada
 *    nivel pertenece a una sola familia, asi que nada se encima.
 */
function computeLayout(roots, pathKeys, sizeOf, expandAll) {
  const focusKey = pathKeys.length ? pathKeys[pathKeys.length - 1] : null;
  // 1) Familias visibles, de arriba hacia abajo
  const fams = [];
  let focusSel = null;
  let list = sortSubordinados(roots);
  let parentKey = null;
  let parentEff = null;
  for (let j = 0; list.length > 0; j += 1) {
    const pathKey = pathKeys[j];
    const pe = parentEff;
    const all = list.map((node) => ({ node, key: nodeKey(node), eff: effectiveNivel(node, pe) }));
    const sel = pathKey ? all.find((m) => m.key === pathKey) : null;
    if (!sel) {
      fams.push({ parentKey, members: all, pathKey: null, hidden: [] });
      break;
    }
    fams.push({
      parentKey,
      members: all.filter((m) => m.eff <= sel.eff),
      pathKey,
      hidden: all.filter((m) => m.eff > sel.eff),
    });
    parentKey = sel.key;
    parentEff = sel.eff;
    // Con toda la estructura abierta, lo de abajo del foco va en otro acomodo
    if (expandAll && sel.key === focusKey) {
      focusSel = sel;
      break;
    }
    list = sortSubordinados(sel.node.subordinados);
  }

  // 1b) Toda la gente que cuelga del foco, con su nivel efectivo
  const subtree = [];
  if (focusSel) {
    const walk = (node, eff) => {
      sortSubordinados(node.subordinados).forEach((ch) => {
        const e = effectiveNivel(ch, eff);
        subtree.push({ node: ch, key: nodeKey(ch), eff: e, parentKey: nodeKey(node) });
        walk(ch, e);
      });
    };
    walk(focusSel.node, focusSel.eff);
  }

  // 2) Filas: un nivel efectivo = una fila, en orden. Cada fila mide lo que su
  //    tarjeta mas alta (asi ningun cargo se corta y la fila queda pareja)
  const bandMembers = new Map();
  const addMember = (m) => {
    if (!bandMembers.has(m.eff)) bandMembers.set(m.eff, []);
    bandMembers.get(m.eff).push(m);
  };
  fams.forEach((f) => f.members.forEach(addMember));
  subtree.forEach(addMember);
  const bands = [...bandMembers.keys()].sort((a, b) => a - b);
  const rowOf = new Map(bands.map((b, i) => [b, i]));
  const rowCardH = bands.map((b) =>
    Math.max(...bandMembers.get(b).map((m) => sizeOf(m.key, false)))
  );
  const rowH = bands.map((b, i) =>
    bandMembers.get(b).some((m) => m.key === focusKey)
      ? Math.max(rowCardH[i], sizeOf(focusKey, true))
      : rowCardH[i]
  );
  const rowY = [];
  bands.forEach((b, i) => rowY.push(i === 0 ? 0 : rowY[i - 1] + rowH[i - 1] + ROW_GAP));

  // 3) Posiciones, familia por familia (cada una se centra bajo su jefe)
  const nodes = [];
  const links = [];
  const chips = [];
  const pos = new Map();
  const lastLevel = pathKeys.length - 1;
  const teamItems = [];

  fams.forEach((fam, j) => {
    const parent = fam.parentKey ? pos.get(fam.parentKey) : null;
    const cx = parent ? parent.x + CARD_W / 2 : 0;
    const isTeam = !fam.pathKey;
    const byEff = new Map();
    fam.members.forEach((m) => {
      if (!byEff.has(m.eff)) byEff.set(m.eff, []);
      byEff.get(m.eff).push(m);
    });
    const effs = [...byEff.keys()].sort((a, b) => a - b);

    effs.forEach((eff, ri) => {
      const row = byEff.get(eff);
      const level = rowOf.get(eff);
      const y = rowY[level];
      const split = Boolean(parent) && ri < effs.length - 1;
      const xs = rowXs(row.length, cx, split, ri);
      const midIdx = (row.length - 1) / 2;

      row.forEach((m, i) => {
        const selected = m.key === fam.pathKey;
        let role = isTeam ? 'team' : 'peer';
        if (selected) role = j === lastLevel ? 'focus' : 'path';
        const item = {
          key: m.key,
          node: m.node,
          level,
          order: Math.abs(i - midIdx),
          role,
          x: xs[i],
          y,
          h: role === 'focus' ? sizeOf(m.key, true) : rowCardH[level],
          parentKey: fam.parentKey,
        };
        nodes.push(item);
        pos.set(m.key, item);
        if (isTeam) teamItems.push(item);
        if (parent) {
          links.push({
            key: `${parent.key}>${m.key}`,
            level,
            order: item.order,
            kind: selected ? 'thread' : isTeam ? 'team' : 'peer',
            color: m.node.tipo === 'vacante' ? VACANTE_COLOR : getGerenciaColor(m.node.gerencia),
            d: elbow(cx, parent.y + parent.h, item.x + CARD_W / 2, y),
          });
        }
      });
    });

    // Lo que no cabe en este eslabon queda resumido bajo su jefe
    if (parent && fam.hidden.length) {
      chips.push({
        key: `otros-${parent.key}`,
        parentKey: parent.key,
        count: fam.hidden.length,
        names: fam.hidden.map((m) => m.node.nombre).join(', '),
        x: cx + 10,
        y: parent.y + parent.h + 12,
      });
    }
  });

  // 3b) Toda la estructura bajo el foco, en arbol
  if (focusSel && subtree.length) {
    const focusItem = pos.get(focusKey);
    const fcx = focusItem.x + CARD_W / 2;
    const byKeySub = new Map(subtree.map((m) => [m.key, m]));
    const tidy = tidyLayout(focusSel.node, focusSel.eff, rowOf);
    tidy.items.forEach((it) => {
      const m = byKeySub.get(it.key);
      if (!m) return; // el propio foco
      const level = rowOf.get(m.eff);
      const cx = fcx + it.dx;
      const item = {
        key: m.key,
        node: m.node,
        level,
        // el despliegue sale del centro hacia afuera
        order: Math.min(12, Math.abs(cx - fcx) / (CARD_W + GAP_X)),
        role: 'team',
        x: cx - CARD_W / 2,
        y: rowY[level],
        h: rowCardH[level],
        parentKey: m.parentKey,
      };
      nodes.push(item);
      pos.set(m.key, item);
      teamItems.push(item);
    });
    tidy.edges.forEach((e) => {
      const p = pos.get(e.parentKey);
      const c = pos.get(e.childKey);
      links.push({
        key: `${e.parentKey}>${e.childKey}`,
        level: c.level,
        order: c.order,
        kind: 'team',
        color: c.node.tipo === 'vacante' ? VACANTE_COLOR : getGerenciaColor(c.node.gerencia),
        d: elbowAt(
          p.x + CARD_W / 2,
          p.y + p.h,
          c.x + CARD_W / 2,
          c.y,
          rowY[e.busRow] - BUS_OFFSET
        ),
      });
    });
  }

  // Franjas: niveles consecutivos del mismo tipo (30/31/32 = Gerencias) se juntan
  const tiers = [];
  bands.forEach((eff, i) => {
    const tier = tierOf(eff);
    const last = tiers[tiers.length - 1];
    if (last && last.tier === tier) last.rows += 1;
    else tiers.push({ tier, first: i, rows: 1 });
  });
  const tierBands = tiers.map((t, i) => {
    const lastRow = t.first + t.rows - 1;
    const top = rowY[t.first] - SLOT_TOP;
    const bottom = rowY[lastRow] + rowH[lastRow] + ROW_GAP - SLOT_TOP;
    return {
      key: `tier-${t.tier}`,
      label: TIER_LABEL[t.tier] || 'Otros',
      tinted: t.tier % 2 === 1,
      divider: i > 0,
      top,
      height: bottom - top,
    };
  });

  // Encuadre del foco: su tarjeta, la linea que llega desde arriba y su equipo
  const focus = pathKeys.length ? pos.get(pathKeys[lastLevel]) : null;
  const boxOf = (items) => {
    if (!items.length) return null;
    const x = Math.min(...items.map((n) => n.x));
    const y = Math.min(...items.map((n) => n.y));
    return {
      x,
      y,
      w: Math.max(...items.map((n) => n.x + CARD_W)) - x,
      h: Math.max(...items.map((n) => n.y + n.h)) - y,
    };
  };
  let focusBox = boxOf(focus ? [focus, ...teamItems] : teamItems);
  if (focusBox && focus && focus.parentKey) {
    focusBox = { ...focusBox, y: focusBox.y - 48, h: focusBox.h + 48 };
  }
  const bounds = boxOf(nodes);

  // Orden de pintado: pares al fondo, el hilo de la cadena de mando encima
  const rank = { peer: 0, team: 1, thread: 2 };
  links.sort((a, b) => rank[a.kind] - rank[b.kind]);

  // Toda la estructura puede ser muy ancha: se deja alejar mas para verla completa
  const fitMin = focusSel ? ZOOM_MIN : FIT_MIN;

  return { nodes, links, chips, tiers: tierBands, pos, focusBox, bounds, fitMin };
}

// Colores de la tarjeta, opacos para que las franjas no se transparenten
const hexToRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const rgbStr = (c) => `rgb(${c.map((v) => Math.round(v)).join(', ')})`;
const mixWhite = (hex, amount) => rgbStr(hexToRgb(hex).map((v) => 255 - (255 - v) * amount));
const luminance = (c) => {
  const [r, g, b] = c.map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
// Fondo solido del foco: el color de la gerencia, oscurecido lo justo para
// que el texto blanco se lea bien (el ocre de Finanzas, por ejemplo, no daba)
const deepCache = new Map();
function deepen(hex) {
  if (!deepCache.has(hex)) {
    let c = hexToRgb(hex);
    for (let i = 0; i < 14 && 1.05 / (luminance(c) + 0.05) < 5.5; i += 1) {
      c = c.map((v) => v * 0.92);
    }
    deepCache.set(hex, rgbStr(c));
  }
  return deepCache.get(hex);
}

// ring: 'tile' = aro blanco y de color (tarjeta tenida), 'dark' = aro blanco
// (foco de color); sin ring, el aro de color de siempre (riel de linaje)
function Avatar({ node, size, ringWidth = 2, ring }) {
  const isVacante = node.tipo === 'vacante';
  const color = getGerenciaColor(node.gerencia);
  const box = { width: size, height: size };
  let shadow;
  if (ring === 'tile') shadow = `0 0 0 2px #fff, 0 0 0 3.5px ${color}`;
  else if (ring === 'dark') shadow = '0 0 0 3px rgba(255, 255, 255, 0.92)';
  else if (ringWidth) shadow = `0 0 0 ${ringWidth}px ${color}`;

  if (isVacante) {
    return (
      <span
        className="rounded-full bg-amber-50 border border-dashed flex items-center justify-center shrink-0"
        style={{ ...box, borderColor: VACANTE_COLOR }}
      >
        <FaExclamationCircle style={{ fontSize: size * 0.42, color: VACANTE_COLOR }} />
      </span>
    );
  }
  if (node.foto) {
    return (
      <img
        src={node.foto}
        alt=""
        draggable={false}
        className="rounded-full object-cover shrink-0 pointer-events-none bg-white"
        style={{ ...box, boxShadow: shadow }}
      />
    );
  }
  return (
    <span
      className="rounded-full bg-gray-100 flex items-center justify-center shrink-0"
      style={{ ...box, boxShadow: shadow }}
    >
      <FaUserTie className="text-gray-400" style={{ fontSize: size * 0.42 }} />
    </span>
  );
}

// Abanico de mini-avatares: lo que hay debajo de una tarjeta antes de abrirla
function TeamFan({ kids, ringColor }) {
  const shown = kids.slice(0, 5);
  const rest = kids.length - shown.length;
  const ring = { boxShadow: `0 0 0 2px ${ringColor}` };
  return (
    <span className="flex items-center">
      {shown.map((k, i) => (
        <span key={nodeKey(k)} className={i > 0 ? '-ml-1.5' : ''}>
          <span className="block rounded-full" style={ring}>
            <Avatar node={k} size={20} ringWidth={0} />
          </span>
        </span>
      ))}
      {rest > 0 && (
        <span
          className="-ml-1.5 w-5 h-5 rounded-full bg-white/80 flex items-center justify-center text-[9px] font-bold text-gray-600 tabular-nums"
          style={ring}
        >
          +{rest}
        </span>
      )}
    </span>
  );
}

function Badge({ node }) {
  if (node.supervisor_roto) {
    return (
      <span
        className="px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-red-100 text-red-700 whitespace-nowrap"
        title="Su supervisor ya no existe (egresó o el puesto se eliminó). Asignarle uno nuevo."
      >
        Jefe eliminado
      </span>
    );
  }
  if (node.tipo === 'vacante' && node.prioridad) {
    return (
      <span className="px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-amber-100 text-amber-700 whitespace-nowrap">
        {node.prioridad}
      </span>
    );
  }
  return null;
}

const cargoOf = (node) => node.cargo || (node.tipo === 'vacante' ? 'Vacante' : 'Sin cargo');

// Texto de la tarjeta compacta. Lo usan la tarjeta y el medidor: mismo CSS,
// mismo alto. El cargo envuelve en las lineas que haga falta.
function CompactText({ node }) {
  const isVacante = node.tipo === 'vacante';
  const detalle = (node.detalles_cargo || '').trim();
  return (
    <>
      <div
        className={`text-[13.5px] font-semibold leading-[18px] break-words ${
          isVacante ? 'text-amber-800' : 'text-gray-900'
        }`}
      >
        {node.nombre}
      </div>
      <div className="mt-1 text-[11.5px] leading-[15px] text-gray-600 break-words">
        {cargoOf(node)}
      </div>
      {/* Detalle del cargo: la categoria de marca, la region del asesor... */}
      {detalle && (
        <div
          className="mt-1 text-[11px] leading-[14px] font-semibold break-words"
          style={{ color: deepen(getGerenciaColor(node.gerencia)) }}
        >
          {detalle}
        </div>
      )}
    </>
  );
}

function FocusText({ node }) {
  const detalle = (node.detalles_cargo || '').trim();
  const showDepto =
    node.departamento &&
    (node.departamento || '').toLowerCase() !== (node.gerencia || '').toLowerCase();
  return (
    <>
      <div className="text-[16px] font-bold leading-[20px] break-words text-white">
        {node.nombre}
      </div>
      <div className="mt-1 text-[12.5px] leading-[17px] break-words text-white/90">
        {cargoOf(node)}
      </div>
      {detalle && (
        <div className="mt-0.5 text-[12px] leading-[16px] font-semibold break-words text-white">
          {detalle}
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] leading-[14px] font-semibold bg-white/15 text-white">
          <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
          {node.gerencia || 'Sin gerencia'}
        </span>
        {showDepto && (
          <span className="text-[10.5px] leading-[14px] text-white/75 break-words">
            {node.departamento}
          </span>
        )}
      </div>
    </>
  );
}

function CompactBody({ node, footBg }) {
  const kids = sortSubordinados(node.subordinados);
  const color = node.tipo === 'vacante' ? VACANTE_COLOR : getGerenciaColor(node.gerencia);
  return (
    <div className="absolute inset-0 flex flex-col">
      <div
        className="flex flex-col items-center text-center"
        style={{ padding: `${C_PAD_TOP}px ${C_PAD_X}px 0` }}
      >
        <Avatar node={node} size={C_PHOTO} ring="tile" />
        <div className="w-full" style={{ marginTop: C_GAP }}>
          <CompactText node={node} />
        </div>
      </div>
      <div
        className="mt-auto shrink-0 flex items-center justify-between gap-2 px-3 border-t"
        style={{ height: C_FOOT_H, background: footBg, borderColor: tintOf(color, 0.22) }}
      >
        {kids.length > 0 ? (
          <TeamFan kids={kids} ringColor={footBg} />
        ) : (
          <span className="text-[11px] text-gray-400">Sin equipo</span>
        )}
        <span className="flex items-center gap-1.5 min-w-0">
          <Badge node={node} />
          {kids.length > 0 && (
            <span className="text-[11.5px] font-semibold tabular-nums" style={{ color }}>
              {kids.length}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

function FocusStat({ value, label, title, accent }) {
  return (
    <div className="flex-1 min-w-0 flex flex-col items-center justify-center" title={title}>
      <span
        className="text-[18px] font-bold leading-[20px] tabular-nums"
        style={{ color: accent || '#fff' }}
      >
        {value}
      </span>
      <span className="mt-0.5 text-[10px] leading-[12px] text-white/75">{label}</span>
    </div>
  );
}

function FocusBody({ node, stats }) {
  const directos = (node.subordinados || []).length;
  return (
    <div className="absolute inset-0 flex flex-col">
      <div
        className="flex flex-col items-center text-center"
        style={{ padding: `${F_PAD_TOP}px ${F_PAD_X}px 0` }}
      >
        <Avatar node={node} size={F_PHOTO} ring="dark" />
        <div className="w-full" style={{ marginTop: F_GAP }}>
          <FocusText node={node} />
        </div>
      </div>
      <div
        className="mt-auto shrink-0 flex items-stretch border-t divide-x divide-white/15"
        style={{ height: F_STATS_H, borderColor: 'rgba(255, 255, 255, 0.2)' }}
      >
        {directos === 0 && (
          <div className="flex-1 flex items-center justify-center text-[11.5px] text-white/80">
            Sin personas a cargo
          </div>
        )}
        {directos > 0 && (
          <FocusStat
            value={stats?.personas || 0}
            label="personas"
            title="Personas en toda su estructura"
          />
        )}
        {directos > 0 && (
          <FocusStat value={directos} label={directos === 1 ? 'directo' : 'directos'} />
        )}
        {stats?.vac > 0 && (
          <FocusStat
            value={stats.vac}
            label={stats.vac === 1 ? 'vacante' : 'vacantes'}
            accent="#fcd34d"
          />
        )}
      </div>
    </div>
  );
}

// Medidor invisible: el texto de todas las personas con el mismo CSS que la
// tarjeta, para saber cuanto alto necesita cada una antes de pintar el mapa
const TextMeasurer = React.memo(function TextMeasurer({ nodes }) {
  return nodes.map((n) => {
    const k = nodeKey(n);
    return (
      <React.Fragment key={k}>
        <div data-mk={`${k}|c`} className="text-center" style={{ width: CARD_W - 2 * C_PAD_X - BORDER_ROOM }}>
          <CompactText node={n} />
        </div>
        <div data-mk={`${k}|f`} className="text-center" style={{ width: CARD_W - 2 * F_PAD_X - BORDER_ROOM }}>
          <FocusText node={n} />
        </div>
      </React.Fragment>
    );
  });
});

// Todo el cuadro lleva el color de la gerencia: tenido en el equipo, un poco
// mas intenso en la cadena de mando y solido en el foco. Lo usan la tarjeta
// del mapa y la de la hoja impresa.
function cardLook(node, role) {
  const isVacante = node.tipo === 'vacante';
  const base = isVacante ? VACANTE_COLOR : getGerenciaColor(node.gerencia);
  if (role === 'focus') {
    const bg = deepen(base);
    return {
      base,
      bg,
      border: bg,
      shadow: `0 18px 40px -18px ${tintOf(base, 0.75)}, 0 2px 8px rgba(15, 23, 42, 0.10)`,
    };
  }
  if (role === 'path') {
    return {
      base,
      bg: mixWhite(base, 0.16),
      footBg: mixWhite(base, 0.24),
      border: base,
      shadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
    };
  }
  return {
    base,
    bg: mixWhite(base, isVacante ? 0.07 : 0.11),
    footBg: mixWhite(base, isVacante ? 0.12 : 0.18),
    border: tintOf(base, isVacante ? 0.6 : 0.42),
  };
}

const borderClassOf = (node, role) => {
  if (node.tipo === 'vacante' && role !== 'focus') return 'border border-dashed';
  return role === 'path' ? 'border-[1.5px]' : 'border';
};

function MapCard({ item, stats, enterFrom, enterDelay, reduce, pinged, onActivate }) {
  const { node, role } = item;
  const hasKids = (node.subordinados || []).length > 0;
  const isVacante = node.tipo === 'vacante';
  const base = isVacante ? VACANTE_COLOR : getGerenciaColor(node.gerencia);
  const isFocus = role === 'focus';
  const connected = role === 'focus' || role === 'path';
  const hoverable = role === 'team' || role === 'peer';

  // La demora escalonada solo aplica a la entrada; despues todo responde ya
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
  }, []);
  const delay = mountedRef.current || reduce ? 0 : safeDelay(enterDelay);

  // Al salir, la tarjeta se recoge hacia el ancestro que sigue en el mapa
  const variants = {
    gone: (ctx) => {
      const to = ctx && ctx.resolve ? ctx.resolve(item.key) : null;
      if (reduce || !to) return { opacity: 0, transition: { duration: reduce ? 0 : 0.18 } };
      return { x: to.x, y: to.bottom - item.h, opacity: 0, scale: 0.72, transition: EXIT_T };
    },
  };

  const { bg, footBg, border, shadow } = cardLook(node, role);

  let title = `Ver a ${node.nombre}`;
  if (isFocus) title = 'Centrar. Doble click: abrir o cerrar toda su estructura';
  else if (hasKids) title = `Ver el equipo de ${node.nombre}. Doble click: toda su estructura`;

  return (
    <motion.div
      className="absolute left-0 top-0"
      style={{ width: CARD_W, zIndex: (isFocus ? 60 : 50) - item.level, originY: 0 }}
      variants={variants}
      initial={{
        x: enterFrom.x,
        y: enterFrom.y,
        height: item.h,
        opacity: 0,
        scale: enterFrom.scale,
      }}
      animate={{ x: item.x, y: item.y, height: item.h, opacity: 1, scale: 1 }}
      transition={
        reduce
          ? { duration: 0 }
          : {
              default: { ...MOVE_T, delay },
              opacity: { duration: 0.22, delay },
              height: SHAPE_T,
            }
      }
      exit="gone"
    >
      <div
        className={[
          'group relative h-full transition-[opacity,filter] duration-300',
          role === 'peer' ? 'opacity-[.42] saturate-[.35] hover:opacity-100 hover:saturate-100' : '',
        ].join(' ')}
        style={{ '--accent': base, '--b': border }}
      >
        <button
          type="button"
          onClick={(e) => onActivate(item, e)}
          title={title}
          className={[
            'relative block w-full h-full text-left rounded-[14px] overflow-hidden outline-none',
            'border-[color:var(--b)] transition-[border-color,box-shadow,background-color] duration-300',
            'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sura-blue/50',
            borderClassOf(node, role),
            hoverable ? 'hover:border-[color:var(--accent)] hover:shadow-md' : '',
            'cursor-pointer',
          ].join(' ')}
          style={{ background: bg, boxShadow: shadow }}
        >
          {/* Al cambiar entre compacta y foco, el contenido viejo se va de una
              (sin cruce, que dejaba el texto doble) y el nuevo entra suave */}
          <motion.div
            key={isFocus ? 'foco' : 'compacta'}
            className="absolute inset-0"
            initial={mountedRef.current && !reduce ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.06 }}
          >
            {isFocus ? (
              <FocusBody node={node} stats={stats} />
            ) : (
              <CompactBody node={node} footBg={footBg} />
            )}
          </motion.div>
        </button>

        {/* Puerto inferior: de aqui sale su equipo. Relleno cuando esta abierto */}
        {hasKids && (
          <span
            className={[
              'absolute left-1/2 -bottom-[5px] -ml-[5px] w-[10px] h-[10px] rounded-full border-2 pointer-events-none transition-colors duration-200',
              connected ? '' : 'bg-white border-gray-300 group-hover:border-[color:var(--accent)]',
            ].join(' ')}
            style={
              isFocus
                ? { background: '#fff', borderColor: bg }
                : connected
                ? { background: base, borderColor: base }
                : undefined
            }
          />
        )}
      </div>

      {/* Destello cuando se llega desde el buscador */}
      {pinged && (
        <motion.span
          className="absolute -inset-[6px] rounded-[19px] border-2 pointer-events-none"
          style={{ borderColor: base }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0.2, 1, 0.2, 1, 0] }}
          transition={{ duration: reduce ? 0.01 : 2.4, ease: 'easeInOut' }}
        />
      )}
    </motion.div>
  );
}

const LINK_STYLE = {
  thread: { width: 2 },
  team: { width: 1.5, stroke: '#c3ccd8' },
  peer: { width: 1.25, stroke: '#dfe4ea' },
};

function MapLink({ link, delay, reduce }) {
  const style = LINK_STYLE[link.kind];
  const stroke = link.kind === 'thread' ? link.color : style.stroke;
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
  }, []);
  const d0 = mountedRef.current || reduce ? 0 : safeDelay(delay);

  return (
    <motion.path
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={{ d: link.d, pathLength: 0, opacity: 0, stroke, strokeWidth: style.width }}
      animate={{ d: link.d, pathLength: 1, opacity: 1, stroke, strokeWidth: style.width }}
      exit={{
        pathLength: 0,
        opacity: 0,
        transition: { duration: reduce ? 0 : 0.22, ease: 'easeIn' },
      }}
      transition={
        reduce
          ? { duration: 0 }
          : {
              d: SHAPE_T,
              pathLength: { duration: 0.5, ease: [0.33, 1, 0.68, 1], delay: d0 },
              opacity: { duration: 0.08, delay: d0 },
              stroke: { duration: 0.3 },
              strokeWidth: { duration: 0.3 },
            }
      }
    />
  );
}

const BAND_T = { type: 'spring', stiffness: 240, damping: 30 };
const LABEL_MIN_Y = 64; // debajo del riel de linaje

// Franja de nivel a todo lo ancho, con su nombre pegado al borde izquierdo.
// Vive en coordenadas de pantalla: sigue a la camara en Y pero no escala.
function TierBand({ tier, camY, camS, reduce }) {
  const top = useMotionValue(tier.top);
  const height = useMotionValue(tier.height);
  useEffect(() => {
    if (reduce) {
      top.set(tier.top);
      height.set(tier.height);
      return undefined;
    }
    const a = animate(top, tier.top, BAND_T);
    const b = animate(height, tier.height, BAND_T);
    return () => {
      a.stop();
      b.stop();
    };
  }, [tier.top, tier.height, reduce, top, height]);

  const y = useTransform([camY, camS, top], ([cy, s, t]) => cy + t * s);
  const h = useTransform([camS, height], ([s, hh]) => hh * s);
  // El nombre se queda arriba de la pantalla mientras su franja siga visible
  const labelY = useTransform([camY, camS, top, height], ([cy, s, t, hh]) =>
    clamp(cy + t * s + 10, LABEL_MIN_Y, cy + (t + hh) * s - 30)
  );
  const fade = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: reduce ? 0 : 0.3 },
  };

  return (
    <>
      <motion.div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 pointer-events-none"
        style={{
          y,
          height: h,
          zIndex: 0,
          background: tier.tinted ? 'rgba(148, 163, 184, 0.09)' : 'transparent',
          borderTop: tier.divider ? '1px solid rgba(148, 163, 184, 0.32)' : 'none',
        }}
        {...fade}
      />
      <motion.div
        className="absolute left-3 top-0 pointer-events-none"
        style={{ y: labelY, zIndex: 20 }}
        {...fade}
      >
        <span className="inline-block px-2 py-0.5 rounded-md bg-[#f6f7f9]/90 text-[11px] font-semibold text-slate-500 whitespace-nowrap">
          {tier.label}
        </span>
      </motion.div>
    </>
  );
}

// Reportes de otros niveles que no se dibujan en un eslabon de arriba
function OtherLevelsChip({ chip, onGo, reduce }) {
  return (
    <motion.button
      type="button"
      onClick={() => onGo(chip.parentKey)}
      title={`Ver todo su equipo: ${chip.names}`}
      className="absolute left-0 top-0 inline-flex items-center px-2 rounded-full bg-white border border-dashed border-slate-300 text-[10.5px] font-semibold text-slate-500 whitespace-nowrap hover:border-slate-400 hover:text-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue/40"
      style={{ height: CHIP_H, zIndex: 70 }}
      initial={{ x: chip.x, y: chip.y - 6, opacity: 0 }}
      animate={{ x: chip.x, y: chip.y, opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: reduce ? 0 : 0.15 } }}
      transition={reduce ? { duration: 0 } : { ...MOVE_T, delay: 0.2 }}
    >
      +{chip.count} en otros niveles
    </motion.button>
  );
}

// ---------------------------------------------------------------------------
// Hoja para imprimir: el mismo mapa que se ve (cadena de mando, pares y el
// equipo del foco) en una hoja horizontal con encabezado SURA y leyenda.
// Vive en un portal directo en <body>: oculto en pantalla, unico al imprimir.
// ---------------------------------------------------------------------------
const PRINT_W = 1000; // ancho util de carta/A4 horizontal con margenes de 10mm
const PRINT_MAP_H = 560; // alto que le queda al mapa entre encabezado y pie
const PRINT_GUTTER = 128; // columna de los nombres de nivel
const PRINT_PAD = 28;

const PRINT_CSS = `
@media print {
  @page { size: landscape; margin: 10mm; }
  html, body { background: #fff !important; }
  body > *:not(#org-map-print) { display: none !important; }
  #org-map-print {
    display: block !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
`;

function PrintCard({ item, stats, ox, oy }) {
  const { node, role } = item;
  const look = cardLook(node, role);
  const hasKids = (node.subordinados || []).length > 0;
  let port = { background: '#fff', borderColor: '#d1d5db' };
  if (role === 'focus') port = { background: '#fff', borderColor: look.bg };
  else if (role === 'path') port = { background: look.base, borderColor: look.base };
  return (
    <div
      className="absolute"
      style={{
        left: item.x - ox,
        top: item.y - oy,
        width: CARD_W,
        height: item.h,
        opacity: role === 'peer' ? 0.5 : 1,
      }}
    >
      <div
        className={`relative w-full h-full rounded-[14px] overflow-hidden ${borderClassOf(node, role)}`}
        style={{ background: look.bg, borderColor: look.border, boxShadow: look.shadow }}
      >
        {role === 'focus' ? (
          <FocusBody node={node} stats={stats} />
        ) : (
          <CompactBody node={node} footBg={look.footBg} />
        )}
      </div>
      {hasKids && (
        <span
          className="absolute left-1/2 -bottom-[5px] -ml-[5px] w-[10px] h-[10px] rounded-full border-2"
          style={port}
        />
      )}
    </div>
  );
}

function PrintSheet({ layout, statsMap, focusNode, totals }) {
  const b = layout.bounds;
  if (!b) return null;
  const ox = b.x - PRINT_PAD;
  const oy = b.y - PRINT_PAD - 10;
  const mapW = b.w + PRINT_PAD * 2;
  const mapH = b.h + PRINT_PAD * 2 + 10;
  const s = Math.min((PRINT_W - PRINT_GUTTER) / mapW, PRINT_MAP_H / mapH, 1);
  const areaH = mapH * s;
  const left = PRINT_GUTTER + (PRINT_W - PRINT_GUTTER - mapW * s) / 2;

  const accent = focusNode
    ? focusNode.tipo === 'vacante'
      ? VACANTE_COLOR
      : getGerenciaColor(focusNode.gerencia)
    : '#003366';
  const accentDeep = deepen(accent);
  const st = focusNode ? statsMap.get(nodeKey(focusNode)) : null;
  const directos = focusNode ? (focusNode.subordinados || []).length : 0;
  const numbers = focusNode
    ? [
        { v: st?.personas || 0, l: 'personas' },
        { v: directos, l: directos === 1 ? 'directo' : 'directos' },
        ...(st?.vac ? [{ v: st.vac, l: st.vac === 1 ? 'vacante' : 'vacantes', vac: true }] : []),
      ]
    : [
        { v: totals.personas, l: 'personas' },
        ...(totals.vac ? [{ v: totals.vac, l: 'vacantes', vac: true }] : []),
      ];
  const fecha = new Intl.DateTimeFormat('es-VE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());

  // Leyenda: las gerencias que aparecen en la hoja, con su color
  const legend = [];
  const seen = new Set();
  layout.nodes.forEach(({ node }) => {
    if (node.tipo === 'vacante') return;
    const k = normalizeGerencia(node.gerencia || 'Sin gerencia');
    if (seen.has(k)) return;
    seen.add(k);
    legend.push({ nombre: node.gerencia || 'Sin gerencia', color: getGerenciaColor(node.gerencia) });
  });
  const hasVac = layout.nodes.some(({ node }) => node.tipo === 'vacante');

  return (
    <div className="bg-white text-gray-900 font-sans" style={{ width: PRINT_W }}>
      <header
        className="flex items-end justify-between gap-8 pb-4 mb-4"
        style={{ borderBottom: `3px solid ${accentDeep}` }}
      >
        <div className="flex items-center gap-5 min-w-0">
          <img src="/logo_sura.png" alt="SURA de Venezuela" className="h-12 w-auto shrink-0" />
          <div className="min-w-0 pl-5 border-l border-gray-200">
            <div className="text-[24px] leading-tight font-extrabold tracking-tight">
              Estructura Organizativa
            </div>
            <div className="mt-1 flex items-baseline gap-2 text-[13px] min-w-0">
              {focusNode ? (
                <>
                  <span className="font-bold" style={{ color: accentDeep }}>
                    {focusNode.nombre}
                  </span>
                  <span className="text-gray-500">{cargoOf(focusNode)}</span>
                </>
              ) : (
                <span className="text-gray-500">Vista general</span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-baseline justify-end gap-5">
            {numbers.map((n) => (
              <span key={n.l} className="whitespace-nowrap">
                <span
                  className="text-[26px] font-extrabold leading-none tabular-nums"
                  style={{ color: n.vac ? VACANTE_COLOR : accentDeep }}
                >
                  {n.v}
                </span>
                <span className="ml-1.5 text-[11px] text-gray-500">{n.l}</span>
              </span>
            ))}
          </div>
          <div className="mt-1.5 text-[11px] text-gray-400">{fecha}</div>
        </div>
      </header>

      <div
        className="relative overflow-hidden rounded-xl"
        style={{ width: PRINT_W, height: areaH, background: '#f8f9fb' }}
      >
        {layout.tiers.map((t) => {
          const top = (t.top - oy) * s;
          return (
            <div
              key={t.key}
              className="absolute inset-x-0"
              style={{
                top,
                height: t.height * s,
                background: t.tinted ? 'rgba(148, 163, 184, 0.11)' : 'transparent',
                borderTop: t.divider ? '1px solid rgba(148, 163, 184, 0.38)' : 'none',
              }}
            >
              <span
                className="absolute left-3 text-[10.5px] font-semibold text-slate-500 whitespace-nowrap"
                style={{ top: Math.max(0, -top) + 8 }}
              >
                {t.label}
              </span>
            </div>
          );
        })}

        <div
          className="absolute top-0"
          style={{
            left,
            width: mapW,
            height: mapH,
            transform: `scale(${s})`,
            transformOrigin: '0 0',
          }}
        >
          <svg className="absolute left-0 top-0 overflow-visible" width="1" height="1">
            <g transform={`translate(${-ox} ${-oy})`}>
              {layout.links.map((l) => {
                const st2 = LINK_STYLE[l.kind];
                return (
                  <path
                    key={l.key}
                    d={l.d}
                    fill="none"
                    stroke={l.kind === 'thread' ? l.color : st2.stroke}
                    strokeWidth={st2.width}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                );
              })}
            </g>
          </svg>
          {layout.nodes.map((item) => (
            <PrintCard
              key={item.key}
              item={item}
              stats={statsMap.get(item.key)}
              ox={ox}
              oy={oy}
            />
          ))}
          {layout.chips.map((c) => (
            <span
              key={c.key}
              className="absolute inline-flex items-center px-2 rounded-full bg-white border border-dashed border-slate-300 text-[10.5px] font-semibold text-slate-500 whitespace-nowrap"
              style={{ left: c.x - ox, top: c.y - oy, height: CHIP_H }}
            >
              +{c.count} en otros niveles
            </span>
          ))}
        </div>
      </div>

      <footer className="flex items-center justify-between gap-6 mt-3 text-[10.5px] text-gray-500">
        <span>Centro Sura</span>
        <span className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
          {legend.map((g) => (
            <span key={g.nombre} className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: g.color }} />
              {g.nombre}
            </span>
          ))}
          {hasVac && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-sm border border-dashed"
                style={{ borderColor: VACANTE_COLOR, background: mixWhite(VACANTE_COLOR, 0.12) }}
              />
              Vacante
            </span>
          )}
        </span>
      </footer>
    </div>
  );
}

// Mismo boton que los de accion del Diagrama (pantalla completa, descarga)
function MapActionButton({ onClick, title, children, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-white border border-gray-300 shadow-sm text-gray-500 transition-all hover:bg-gray-50 hover:border-blue-500 hover:text-blue-500 hover:shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue/40"
    >
      {children}
    </button>
  );
}

function HudButton({ onClick, title, children, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue/40 ${
        active ? 'bg-sura-blue text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
      }`}
    >
      {children}
    </button>
  );
}

// Lectura del zoom: se suscribe sola para no re-renderizar el mapa en cada frame
function ZoomReadout({ scale, onReset }) {
  const [pct, setPct] = useState(() => Math.round(scale.get() * 100));
  useMotionValueEvent(scale, 'change', (v) => setPct(Math.round(v * 100)));
  return (
    <button
      type="button"
      onClick={onReset}
      title="Volver a 100%"
      className="w-12 h-8 rounded-lg text-[11px] font-semibold tabular-nums text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
    >
      {pct}%
    </button>
  );
}

export default function OrgMapView({ roots, loading, error, selectedEmployeeId }) {
  const reduce = useReducedMotion();
  const { byKey, parentOf, stats } = useMemo(() => buildMaps(roots), [roots]);
  const singleRoot = (roots || []).length === 1;
  const defaultFocus = singleRoot ? nodeKey(roots[0]) : null;

  const [focusKey, setFocusKey] = useState(defaultFocus);
  // Doble click: ademas del equipo directo, toda la estructura hacia abajo
  const [expandAll, setExpandAll] = useState(false);
  const [pingKey, setPingKey] = useState(null);
  const effectiveFocus = focusKey && byKey.has(focusKey) ? focusKey : defaultFocus;

  const pathKeys = useMemo(() => {
    const path = [];
    let k = effectiveFocus;
    while (k) {
      path.unshift(k);
      k = parentOf.get(k);
    }
    return path;
  }, [effectiveFocus, parentOf]);

  // Alto de cada tarjeta: el texto medido (o un estimado mientras tanto)
  const allNodes = useMemo(() => [...byKey.values()], [byKey]);
  const measurerRef = useRef(null);
  const [textH, setTextH] = useState(() => new Map());
  const [fontsTick, setFontsTick] = useState(0);
  useEffect(() => {
    const fonts = typeof document !== 'undefined' ? document.fonts : null;
    if (!fonts) return undefined;
    let alive = true;
    const bump = () => alive && setFontsTick((t) => t + 1);
    if (fonts.ready) fonts.ready.then(bump);
    fonts.addEventListener?.('loadingdone', bump);
    return () => {
      alive = false;
      fonts.removeEventListener?.('loadingdone', bump);
    };
  }, []);
  useLayoutEffect(() => {
    const el = measurerRef.current;
    if (!el) return;
    const next = new Map();
    el.querySelectorAll('[data-mk]').forEach((d) => {
      next.set(d.dataset.mk, Math.ceil(d.getBoundingClientRect().height));
    });
    setTextH(next);
  }, [allNodes, fontsTick, loading, error]);
  const sizeOf = useCallback(
    (key, focus) =>
      focus
        ? focusHeight(textH.get(`${key}|f`) ?? TEXT_GUESS_F)
        : compactHeight(textH.get(`${key}|c`) ?? TEXT_GUESS_C),
    [textH]
  );

  const layout = useMemo(
    () => computeLayout(roots || [], pathKeys, sizeOf, expandAll),
    [roots, pathKeys, sizeOf, expandAll]
  );

  // Tarjetas que entran en este render: escalonar por nivel y por cercania al centro
  const prevKeysRef = useRef(new Set());
  const enteringLevel = useMemo(() => {
    let min = Infinity;
    let top = Infinity;
    layout.nodes.forEach((n) => {
      top = Math.min(top, n.level);
      if (!prevKeysRef.current.has(n.key)) min = Math.min(min, n.level);
    });
    // Si ninguna es nueva (el mapa se volvio a montar con gente que ya estaba
    // en la vista anterior) se escalona desde la fila de arriba. Sin esto la
    // demora quedaba en -Infinity, la animacion fallaba y todo quedaba en blanco.
    if (Number.isFinite(min)) return min;
    return Number.isFinite(top) ? top : 0;
  }, [layout]);
  useEffect(() => {
    prevKeysRef.current = new Set(layout.nodes.map((n) => n.key));
  }, [layout]);
  // Mientras carga (o si falla) el mapa se desmonta: al volver, todo entra de nuevo
  useEffect(() => {
    if (loading || error) prevKeysRef.current = new Set();
  }, [loading, error]);

  // Contexto de salida: a donde se recoge cada tarjeta que desaparece
  const exitCtx = useMemo(
    () => ({
      resolve: (key) => {
        let k = parentOf.get(key);
        while (k) {
          const p = layout.pos.get(k);
          if (p) return { x: p.x, bottom: p.y + p.h };
          k = parentOf.get(k);
        }
        return null;
      },
    }),
    [layout, parentOf]
  );

  // ---------------------------------------------------------------------------
  // Camara
  // ---------------------------------------------------------------------------
  const camX = useMotionValue(0);
  const camY = useMotionValue(0);
  const camS = useMotionValue(1);
  const camAnims = useRef([]);
  const [viewportEl, setViewportEl] = useState(null);
  const [vp, setVp] = useState({ w: 0, h: 0 });
  const vpRef = useRef(vp);
  vpRef.current = vp;

  const stopCam = useCallback(() => {
    camAnims.current.forEach((a) => a.stop());
    camAnims.current = [];
  }, []);

  const moveCam = useCallback(
    (tx, ty, ts, instant) => {
      stopCam();
      if (instant || reduce) {
        camX.set(tx);
        camY.set(ty);
        camS.set(ts);
        return;
      }
      camAnims.current = [
        animate(camX, tx, CAMERA_T),
        animate(camY, ty, CAMERA_T),
        animate(camS, ts, CAMERA_T),
      ];
    },
    [camX, camY, camS, stopCam, reduce]
  );

  const fitBox = useCallback(
    (box, { min = FIT_MIN, max = FIT_MAX, instant = false } = {}) => {
      const { w: vw, h: vh } = vpRef.current;
      if (!vw || !vh || !box) return;
      const availW = Math.max(120, vw - PAD_X * 2);
      const availH = Math.max(120, vh - PAD_TOP - PAD_BOTTOM);
      const s = clamp(Math.min(availW / box.w, availH / box.h), min, max);
      const tx = vw / 2 - (box.x + box.w / 2) * s;
      // Si no cabe en alto, se ancla arriba: primero el foco, el equipo se recorre
      const ty =
        box.h * s > availH
          ? PAD_TOP - box.y * s
          : PAD_TOP + availH / 2 - (box.y + box.h / 2) * s;
      moveCam(tx, ty, s, instant);
    },
    [moveCam]
  );

  const zoomAround = useCallback(
    (px, py, target, animated) => {
      const s0 = camS.get();
      const s = clamp(target, ZOOM_MIN, ZOOM_MAX);
      const wx = (px - camX.get()) / s0;
      const wy = (py - camY.get()) / s0;
      if (animated) moveCam(px - wx * s, py - wy * s, s);
      else {
        camS.set(s);
        camX.set(px - wx * s);
        camY.set(py - wy * s);
      }
    },
    [camX, camY, camS, moveCam]
  );

  const zoomBy = (factor) =>
    zoomAround(vpRef.current.w / 2, vpRef.current.h / 2, camS.get() * factor, true);
  const recenter = () => fitBox(layout.focusBox, { min: layout.fitMin });
  const fitAll = () => fitBox(layout.bounds, { min: ZOOM_MIN, max: 1 });

  // Tamano del lienzo
  useEffect(() => {
    if (!viewportEl) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setVp({ w: Math.round(width), h: Math.round(height) });
    });
    ro.observe(viewportEl);
    return () => ro.disconnect();
  }, [viewportEl]);

  // Reiniciar al cambiar los datos (filtros). Va antes del encuadre para que
  // este, en el mismo commit, ya sepa que debe saltar sin viaje.
  const didFitRef = useRef(false);
  useEffect(() => {
    didFitRef.current = false;
    setFocusKey(singleRoot ? nodeKey(roots[0]) : null);
    setExpandAll(false);
  }, [roots, singleRoot]);

  // Encuadrar el foco cada vez que cambia el mapa; la primera vez sin viaje
  useEffect(() => {
    if (!vp.w || !vp.h) return;
    fitBox(layout.focusBox, { instant: !didFitRef.current, min: layout.fitMin });
    didFitRef.current = true;
  }, [layout, vp.w, vp.h, fitBox]);

  // Buscador: si tiene equipo se abre el suyo; si no, el de su jefe, y destella
  useEffect(() => {
    if (!selectedEmployeeId) return undefined;
    const k = `empleado-${selectedEmployeeId}`;
    const node = byKey.get(k);
    if (!node) return undefined;
    const hasKids = (node.subordinados || []).length > 0;
    setFocusKey(hasKids ? k : parentOf.get(k) || null);
    setExpandAll(false);
    setPingKey(k);
    const t = setTimeout(() => setPingKey(null), 2600);
    return () => clearTimeout(t);
  }, [selectedEmployeeId, byKey, parentOf]);

  const goTo = useCallback((key) => {
    setFocusKey(key);
    setExpandAll(false);
  }, []);

  // Doble click sobre alguien: se enfoca y se abre toda su estructura. Sobre el
  // foco ya abierto, la cierra y vuelve a su equipo directo.
  const expandFrom = (key) => {
    if (!byKey.has(key)) return;
    if (key === effectiveFocus && expandAll) {
      setExpandAll(false);
      return;
    }
    setFocusKey(key);
    setExpandAll(true);
  };
  // El doble click se detecta por tiempo y posicion del puntero, no por el
  // elemento: con el primer click la camara ya se mueve y el segundo caeria
  // sobre otra tarjeta
  const lastClickRef = useRef(null);

  // Pantalla completa solo del lienzo (sin la barra de filtros): para proyectar
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const onChange = () =>
      setIsFull(Boolean(viewportEl) && document.fullscreenElement === viewportEl);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [viewportEl]);
  const toggleFull = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (viewportEl && viewportEl.requestFullscreen) await viewportEl.requestFullscreen();
    } catch (err) {
      /* el navegador no lo permitio */
    }
  };

  // Imprimir: se monta la hoja, se esperan las fotos y se abre el dialogo.
  // El titulo de la pestana se vuelve el nombre del PDF al guardarlo.
  const [printing, setPrinting] = useState(false);
  const printTitleRef = useRef('Estructura Organizativa');
  const startPrint = () => {
    const f = effectiveFocus ? byKey.get(effectiveFocus) : null;
    printTitleRef.current = f
      ? `Estructura Organizativa - ${f.nombre}`
      : 'Estructura Organizativa';
    setPrinting(true);
  };
  useEffect(() => {
    if (!printing) return undefined;
    let cancelled = false;
    const prevTitle = document.title;
    const finish = () => {
      document.title = prevTitle;
      setPrinting(false);
    };
    window.addEventListener('afterprint', finish);
    document.title = printTitleRef.current;
    const raf = requestAnimationFrame(async () => {
      const sheet = document.getElementById('org-map-print');
      const imgs = sheet ? [...sheet.querySelectorAll('img')] : [];
      await Promise.race([
        Promise.all(
          imgs.map((img) =>
            img.complete
              ? null
              : new Promise((resolve) => {
                  img.addEventListener('load', resolve, { once: true });
                  img.addEventListener('error', resolve, { once: true });
                })
          )
        ),
        new Promise((resolve) => setTimeout(resolve, 2500)),
      ]);
      if (!cancelled) window.print();
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('afterprint', finish);
      document.title = prevTitle;
    };
  }, [printing]);

  const totals = useMemo(() => {
    let personas = 0;
    let vac = 0;
    byKey.forEach((n) => {
      if (n.tipo === 'vacante') vac += 1;
      else personas += 1;
    });
    return { personas, vac };
  }, [byKey]);

  const onActivate = (item, e) => {
    lastClickRef.current = {
      key: item.key,
      t: performance.now(),
      x: e ? e.clientX : 0,
      y: e ? e.clientY : 0,
    };
    if (item.role === 'focus') recenter();
    else goTo(item.key);
  };

  // Rueda: zoom alrededor del cursor (igual que el diagrama clasico)
  useEffect(() => {
    if (!viewportEl) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      stopCam();
      const r = viewportEl.getBoundingClientRect();
      const unit = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 400 : 1;
      const k = e.ctrlKey ? 0.01 : 0.0022;
      zoomAround(
        e.clientX - r.left,
        e.clientY - r.top,
        camS.get() * Math.exp(-e.deltaY * unit * k),
        false
      );
    };
    viewportEl.addEventListener('wheel', onWheel, { passive: false });
    return () => viewportEl.removeEventListener('wheel', onWheel);
  }, [viewportEl, stopCam, zoomAround, camS]);

  // Arrastre para mover (un dedo o mouse) y pellizco para zoom (dos dedos)
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const suppressClick = useRef(false);
  const [grabbing, setGrabbing] = useState(false);

  const localPoint = (e) => {
    const r = viewportEl.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const p = localPoint(e);
    pointers.current.set(e.pointerId, p);
    stopCam();
    if (pointers.current.size === 1) {
      gesture.current = {
        kind: 'pan',
        sx: p.x,
        sy: p.y,
        cx: camX.get(),
        cy: camY.get(),
        moved: false,
      };
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = {
        kind: 'pinch',
        d0: Math.max(1, dist(a, b)),
        m0: mid(a, b),
        s0: camS.get(),
        cx: camX.get(),
        cy: camY.get(),
        moved: true,
      };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const p = localPoint(e);
    pointers.current.set(e.pointerId, p);
    const g = gesture.current;
    if (!g) return;
    if (g.kind === 'pan') {
      const dx = p.x - g.sx;
      const dy = p.y - g.sy;
      if (!g.moved) {
        if (Math.hypot(dx, dy) < 5) return;
        g.moved = true;
        setGrabbing(true);
        try {
          viewportEl.setPointerCapture(e.pointerId);
        } catch (err) {
          /* el puntero ya se solto */
        }
      }
      camX.set(g.cx + dx);
      camY.set(g.cy + dy);
    } else if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const s = clamp((g.s0 * dist(a, b)) / g.d0, ZOOM_MIN, ZOOM_MAX);
      const m = mid(a, b);
      const wx = (g.m0.x - g.cx) / g.s0;
      const wy = (g.m0.y - g.cy) / g.s0;
      camS.set(s);
      camX.set(m.x - wx * s);
      camY.set(m.y - wy * s);
    }
  };

  const onPointerEnd = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (g && g.moved) {
      // El click que sigue a un arrastre no debe abrir la tarjeta de abajo
      suppressClick.current = true;
      setTimeout(() => {
        suppressClick.current = false;
      }, 0);
    }
    if (pointers.current.size === 0) {
      gesture.current = null;
      setGrabbing(false);
    } else if (g && g.kind === 'pinch') {
      const [p] = [...pointers.current.values()];
      gesture.current = { kind: 'pan', sx: p.x, sy: p.y, cx: camX.get(), cy: camY.get(), moved: true };
    }
  };

  const onClickCapture = (e) => {
    if (suppressClick.current) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    const last = lastClickRef.current;
    if (
      last &&
      performance.now() - last.t < DOUBLE_CLICK_MS &&
      Math.hypot(e.clientX - last.x, e.clientY - last.y) < 12
    ) {
      e.stopPropagation();
      e.preventDefault();
      lastClickRef.current = null;
      expandFrom(last.key);
    }
  };

  // Teclado: arriba/Esc sube, abajo entra, izquierda/derecha salta entre pares
  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const focusItem = effectiveFocus ? layout.pos.get(effectiveFocus) : null;
      let target;
      if (e.key === 'ArrowUp' || e.key === 'Escape') {
        if (!effectiveFocus) return;
        const pk = parentOf.get(effectiveFocus);
        if (pk) target = pk;
        else if (!singleRoot) target = null;
        else return;
      } else if (e.key === 'ArrowDown') {
        const first = layout.nodes.find((n) => n.role === 'team');
        if (!first) return;
        target = first.key;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        if (!focusItem) return;
        const row = layout.nodes.filter((n) => n.level === focusItem.level);
        const i = row.findIndex((n) => n.key === focusItem.key);
        const j = i + (e.key === 'ArrowLeft' ? -1 : 1);
        if (i < 0 || j < 0 || j >= row.length) return;
        target = row[j].key;
      } else {
        return;
      }
      e.preventDefault();
      goTo(target);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [effectiveFocus, layout, parentOf, singleRoot, goTo]);

  // Cuadricula de puntos que viaja con la camara: da la sensacion de lienzo
  const gridSize = useTransform(camS, (s) => {
    let size = 22 * s;
    while (size < 12) size *= 2;
    return `${size}px ${size}px`;
  });
  const gridPos = useMotionTemplate`${camX}px ${camY}px`;

  if (loading) return <OrgStateBox spinner text="Cargando estructura..." />;
  if (error) return <OrgStateBox text={error} tone="error" />;
  if (!roots || roots.length === 0) {
    return <OrgStateBox text="No hay empleados para esta selección." icon={<FaSitemap />} />;
  }

  const pathNodes = pathKeys.map((k) => byKey.get(k)).filter(Boolean);
  const canGoUp = Boolean(effectiveFocus) && (pathKeys.length > 1 || !singleRoot);
  const goUp = () => {
    if (!effectiveFocus) return;
    goTo(parentOf.get(effectiveFocus) || null);
  };
  const stopPan = (e) => e.stopPropagation();

  return (
    <div
      ref={setViewportEl}
      className="absolute inset-0 overflow-hidden select-none bg-[#f6f7f9]"
      style={{ touchAction: 'none', cursor: grabbing ? 'grabbing' : 'grab' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onClickCapture={onClickCapture}
    >
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #d3dae3 1px, transparent 1.3px)',
          backgroundSize: gridSize,
          backgroundPosition: gridPos,
        }}
      />

      <div
        ref={measurerRef}
        aria-hidden="true"
        className="absolute left-0 top-0 pointer-events-none"
        style={{ visibility: 'hidden', transform: 'translateX(-300vw)' }}
      >
        <TextMeasurer nodes={allNodes} />
      </div>

      {/* Franjas de nivel (fondo) y sus nombres (borde izquierdo) */}
      <AnimatePresence>
        {layout.tiers.map((tier) => (
          <TierBand key={tier.key} tier={tier} camY={camY} camS={camS} reduce={reduce} />
        ))}
      </AnimatePresence>

      {/* Mundo: todo lo que vive en coordenadas del mapa */}
      <motion.div
        className="absolute left-0 top-0"
        style={{ x: camX, y: camY, scale: camS, transformOrigin: '0 0', zIndex: 1 }}
      >
        <svg
          className="absolute left-0 top-0 overflow-visible pointer-events-none"
          width="1"
          height="1"
          aria-hidden="true"
        >
          <AnimatePresence>
            {layout.links.map((link) => (
              <MapLink
                key={link.key}
                link={link}
                reduce={reduce}
                delay={(link.level - enteringLevel) * 0.14 + link.order * 0.03}
              />
            ))}
          </AnimatePresence>
        </svg>

        <AnimatePresence custom={exitCtx}>
          {layout.nodes.map((item) => {
            const parent = item.parentKey ? layout.pos.get(item.parentKey) : null;
            const enterFrom = parent
              ? { x: parent.x, y: parent.y + parent.h - item.h, scale: 0.72 }
              : { x: item.x, y: item.y - 14, scale: 0.96 };
            return (
              <MapCard
                key={item.key}
                item={item}
                stats={stats.get(item.key)}
                enterFrom={enterFrom}
                enterDelay={0.08 + (item.level - enteringLevel) * 0.14 + item.order * 0.035}
                reduce={reduce}
                pinged={pingKey === item.key}
                onActivate={onActivate}
              />
            );
          })}
        </AnimatePresence>

        <AnimatePresence>
          {layout.chips.map((chip) => (
            <OtherLevelsChip key={chip.key} chip={chip} onGo={goTo} reduce={reduce} />
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Riel de linaje: la cadena de mando, clickeable */}
      <div
        className="absolute top-3 left-3 z-30 max-w-[calc(100%-140px)] cursor-default"
        onPointerDown={stopPan}
      >
        <div className="flex items-center gap-1 p-1 rounded-full bg-white/90 backdrop-blur border border-gray-200/80 shadow-sm overflow-x-auto">
          {canGoUp && (
            <button
              type="button"
              onClick={goUp}
              title="Subir un nivel"
              aria-label="Subir un nivel"
              className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            >
              <FaArrowUp size={11} />
            </button>
          )}
          {!singleRoot && (
            <button
              type="button"
              onClick={() => goTo(null)}
              title="Vista general"
              aria-label="Vista general"
              className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                !effectiveFocus
                  ? 'bg-sura-blue text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              <FaSitemap size={11} />
            </button>
          )}
          {pathNodes.map((n, i) => {
            const last = i === pathNodes.length - 1;
            const k = nodeKey(n);
            return (
              <React.Fragment key={k}>
                {(i > 0 || !singleRoot) && <span className="shrink-0 w-2.5 h-px bg-gray-300" />}
                <button
                  type="button"
                  onClick={() => (last ? recenter() : goTo(k))}
                  title={`${n.nombre}${n.cargo ? ` (${n.cargo})` : ''}`}
                  className={`shrink-0 flex items-center gap-2 rounded-full transition-opacity ${
                    last ? 'pr-3 pl-0.5 py-0.5' : 'p-0.5 opacity-75 hover:opacity-100'
                  }`}
                >
                  <Avatar node={n} size={last ? 26 : 22} ringWidth={last ? 2 : 1.5} />
                  {last && (
                    <span className="text-[12.5px] font-semibold text-gray-700 truncate max-w-[180px]">
                      {n.nombre}
                    </span>
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <p
        className="hidden md:block absolute bottom-4 left-4 z-30 text-[11px] text-gray-400 pointer-events-none"
      >
        Click enfoca, doble click abre toda su estructura. Arrastra para moverte y usa la rueda
        para acercar
      </p>

      {/* Pantalla completa e imprimir, como en el Diagrama */}
      <div
        className="absolute top-3 right-3 z-30 flex items-center gap-2 cursor-default"
        onPointerDown={stopPan}
      >
        <MapActionButton
          onClick={toggleFull}
          title={isFull ? 'Salir de pantalla completa' : 'Pantalla completa'}
        >
          {isFull ? <FaCompress size={14} /> : <FaExpand size={14} />}
        </MapActionButton>
        <MapActionButton onClick={startPrint} title="Imprimir" disabled={printing}>
          <FaPrint size={14} />
        </MapActionButton>
      </div>

      {printing &&
        createPortal(
          <div id="org-map-print" style={{ display: 'none' }}>
            <style>{PRINT_CSS}</style>
            <PrintSheet
              layout={layout}
              statsMap={stats}
              focusNode={effectiveFocus ? byKey.get(effectiveFocus) : null}
              totals={totals}
            />
          </div>,
          document.body
        )}

      {/* Controles de camara */}
      <div
        className="absolute bottom-3 right-3 z-30 flex items-center gap-0.5 p-1 rounded-xl bg-white/90 backdrop-blur border border-gray-200/80 shadow-sm cursor-default"
        onPointerDown={stopPan}
      >
        <HudButton onClick={() => zoomBy(1 / 1.25)} title="Alejar">
          <FaMinus size={11} />
        </HudButton>
        <ZoomReadout
          scale={camS}
          onReset={() => zoomAround(vpRef.current.w / 2, vpRef.current.h / 2, 1, true)}
        />
        <HudButton onClick={() => zoomBy(1.25)} title="Acercar">
          <FaPlus size={11} />
        </HudButton>
        <span className="w-px h-5 bg-gray-200 mx-1" />
        <HudButton onClick={recenter} title="Centrar en el equipo actual">
          <FaCrosshairs size={13} />
        </HudButton>
        <HudButton onClick={fitAll} title="Ver todo lo abierto">
          <FaExpandArrowsAlt size={12} />
        </HudButton>
      </div>
    </div>
  );
}
